import { createSelector } from "reselect"
import { get, find, chain, forEach, keysIn, includes, isEmpty, isFunction, capitalize, first, omit, pick, map, compact, reduce, flatten } from "lodash"
import { getBetaColumnsAndTooltips } from "helpers/tables"
import { percentageFormatter, numberFormatter, makeCurrencyFormatter } from "helpers/formatters/numbers"
import { VIEW_TYPES } from "helpers/base"
import { parseSortOrder } from "helpers/sorts"
import { addCompositionTopXRow, addCompositionTotalRow } from "helpers/analyze"
import { getTickerAndMicFromContributor, isUnmodeled } from "helpers/securities"
import { computeSummarySheetDownload, computeGroupSheetDownload, transformCompareDownloadArray } from "helpers/comparison"
import { getSelectedModelName } from "modules/portfolio-root/selectors/portfolios"
import { getMissingDataFiles, betasAreMissing } from "selectors/missingDataFiles"
import { getSelectedGroup, getPositionsDisplayType } from "./metrics"
import { getItems } from "./items"
import { getCurrency } from "."

const EMPTY_ARRAY = []
const numberOfPositionsPerPage = 100
const getSelectedDate = (state, props) => props.selectedDate
const getPositionSet0 = (state, props) => props.positionSets[0]
const getPositionSet1 = (state, props) => props.positionSets[1]
const getPositionSet2 = (state, props) => props.positionSets[2]
const getPositionSet3 = (state, props) => props.positionSets[3]
const getPositionSet4 = (state, props) => props.positionSets[4]

const getDailyPositionData0 = state => get(state, "global.data.compareCompositionPositionData0.data.model.simulation.composition.0", EMPTY_ARRAY)
const getDailyPositionData1 = state => get(state, "global.data.compareCompositionPositionData1.data.model.simulation.composition.0", EMPTY_ARRAY)
const getDailyPositionData2 = state => get(state, "global.data.compareCompositionPositionData2.data.model.simulation.composition.0", EMPTY_ARRAY)
const getDailyPositionData3 = state => get(state, "global.data.compareCompositionPositionData3.data.model.simulation.composition.0", EMPTY_ARRAY)
const getDailyPositionData4 = state => get(state, "global.data.compareCompositionPositionData4.data.model.simulation.composition.0", EMPTY_ARRAY)

const getStatus0 = state => get(state, "global.data.compareCompositionPositionData0.status")
const getStatus1 = state => get(state, "global.data.compareCompositionPositionData1.status")
const getStatus2 = state => get(state, "global.data.compareCompositionPositionData2.status")
const getStatus3 = state => get(state, "global.data.compareCompositionPositionData3.status")
const getStatus4 = state => get(state, "global.data.compareCompositionPositionData4.status")

const getData0 = state => get(state, "global.data.compareCompositionPositionSet0.data.model.simulation", EMPTY_ARRAY)
const getData1 = state => get(state, "global.data.compareCompositionPositionSet1.data.model.simulation", EMPTY_ARRAY)
const getData2 = state => get(state, "global.data.compareCompositionPositionSet2.data.model.simulation", EMPTY_ARRAY)
const getData3 = state => get(state, "global.data.compareCompositionPositionSet3.data.model.simulation", EMPTY_ARRAY)
const getData4 = state => get(state, "global.data.compareCompositionPositionSet4.data.model.simulation", EMPTY_ARRAY)

const getRawPositionsSorting = state => get(state, "modules.compareComposition.positionsSorting")
const getPositionsPage = state => get(state, "modules.compareComposition.positionsPage")
const getRefEquityDisplayOverride = (index) => (state, props) => get(props, ["refEquityDisplayOverride", index], null)
const getModelId = (_, props) => props.modelId
const getContext = (_, props) => props.context
const getViewType = (_, props) => props.viewType
const getIsActive = createSelector([getViewType], viewType => viewType === VIEW_TYPES.ACTIVE)

function getDailyData(status, selectedDate, { composition, beta }, daily, selectedGroup, positionSet, isActive, refEquityOverride, showNoModeledGMV) {
  if (!composition) return null

  const compositionData = {
    ...find(composition, d => d.date === selectedDate),
    ...daily,
  }

  const betaData = find(beta, d => d.date === selectedDate)

  if (isEmpty(compositionData) || (!showNoModeledGMV && compositionData.modeledGmv === 0) || !positionSet) {
    return null
  }

  let compositionBy
  const compositionByData = selectedGroup.id === "LONG_SHORT" ? compositionData.longShortComposition : compositionData.compositionBy

  // When in active mode and grouping by LONG_SHORT, we want to query the API with OVERWEIGHT_UNDERWEIGHT grouping instead. We also want to display the
  // selected group as OVERWEIGHT_UNDERWEIGHT. Rather than create a new grouping type, we map LONG_SHORT to OVERWEIGHT_UNDERWEIGHT since the grouping
  // types are similar. This saves us from having to swap out the selected grouping when switching between active and absolute modes.
  // See: https://github.com/omegapointresearch/product-arch/blob/master/external-api/over-under-weight.md#part-2--update-ui-to-add-overunder-weight
  if (selectedGroup.id === "LONG_SHORT") {
    const longId = isActive ? "OVERWEIGHT" : "LONG"
    const shortId = isActive ? "UNDERWEIGHT" : "SHORT"
    const longLabel = capitalize(longId)
    const shortLabel = capitalize(shortId)

    if (compositionByData.length > 1) {
      const longShort = chain(compositionByData)
        .mapKeys("id")
        .mapValues(item => Math.abs(item.economicExposure))
        .value()

      const long = get(longShort, longId)
      const short = get(longShort, shortId)

      compositionBy = [
        {
          id: longId,
          label: longLabel,
          value: long,
        },
        {
          id: shortId,
          label: shortLabel,
          value: short,
        },
        {
          id: "NET",
          label: "Net",
          value: long - short,
        },
      ]
    } else {
      const data = first(compositionByData)
      compositionBy = [{
        id: data.id,
        label: `${data.id === longId ? longLabel : shortLabel} Only`,
        value: Math.abs(data.economicExposure),
      }]
    }

    addCompositionTotalRow(compositionBy, selectedGroup.label)
  } else {
    compositionBy = chain(compositionByData)
      .map(item => ({ label: item.description, value: item.economicExposure }))
      .orderBy([item => Math.abs(item.value)], ["desc"])
      .value()

    addCompositionTopXRow(compositionBy, selectedGroup.label)
  }

  let equity
  if (refEquityOverride) {
    equity = isFunction(refEquityOverride) ? refEquityOverride(compositionData) : refEquityOverride
  } else if (compositionData.referenceEquity) {
    equity = compositionData.referenceEquity
  }

  return {
    loading: status,
    summary: {
      gmv: compositionData.gmv,
      equity,
      positionsCount: compositionData.positionsCount,
      percentModeledGmv: compositionData.modeledGmv / compositionData.gmv,
      minConcentration: get(compositionData, "concentration.min"),
      maxConcentration: get(compositionData, "concentration.max"),
      averageMarketCap: get(compositionData, "summaryStats.average.marketCapitalization"),
    },
    beta: {
      predicted: get(betaData, "predicted"),
      historical: get(betaData, "historical"),
    },
    groups: chain(compositionBy)
      .map(item => {
        const absValue = Math.abs(item.value)
        return {
          ...item,
          chartWidth: percentageFormatter(absValue < 100 ? absValue : 100, true),
        }
      })
      .value(),
    positions: chain(compositionData.positions)
      .map(item => {
        const tickerMic = getTickerAndMicFromContributor(item)
        return {
          ...item,
          ...tickerMic,
          isUnmodeled: isUnmodeled(item),
        }
      })
      .value(),
  }
}

function computePositionsSheetDownload(data, compareItems, positions, positionsLabel) {
  let positionsHeaders = ["Position", ...compact(map(compareItems, "label"))]
  let positionsRows = reduce(flatten(positions), (positionResults, position) => (([
    ...positionResults,
    makeTopTenPositionsRow(compareItems, data, position),
  ])), [])

  if (positionsLabel !== "All Positions") {
    positionsHeaders = compact(flatten(map(compareItems, ({ label }) => ["Position", label])))
    positionsRows = transformCompareDownloadArray(compareItems, positions)
  }
  return {
    rows: [positionsHeaders, ...positionsRows],
    name: positionsLabel,
  }
}

function makeTopTenPositionsRow(compareItems, dataRows, position) {
  const { id, formatter } = position || {}
  return [
    id || "Unknown",
    ...map(compareItems, (_, index) => {
      const positions = get(dataRows, `[${index}].positions`, [])
      const position = find(positions, ({ id }))
      if (position) {
        return formatter ? formatter(position.economicExposure) : `${position.economicExposure}%`
      }
      return 0
    }),
  ]
}

const getDailyData0 = createSelector([getStatus0, getSelectedDate, getData0, getDailyPositionData0, getSelectedGroup, getPositionSet0, getIsActive, getRefEquityDisplayOverride(0), () => true], getDailyData)
const getDailyData1 = createSelector([getStatus1, getSelectedDate, getData1, getDailyPositionData1, getSelectedGroup, getPositionSet1, getIsActive, getRefEquityDisplayOverride(1)], getDailyData)
const getDailyData2 = createSelector([getStatus2, getSelectedDate, getData2, getDailyPositionData2, getSelectedGroup, getPositionSet2, getIsActive, getRefEquityDisplayOverride(2)], getDailyData)
const getDailyData3 = createSelector([getStatus3, getSelectedDate, getData3, getDailyPositionData3, getSelectedGroup, getPositionSet3, getIsActive, getRefEquityDisplayOverride(3)], getDailyData)
const getDailyData4 = createSelector([getStatus4, getSelectedDate, getData4, getDailyPositionData4, getSelectedGroup, getPositionSet4, getIsActive, getRefEquityDisplayOverride(4)], getDailyData)

export const getDetailsData = createSelector(
  [getDailyData0, getDailyData1, getDailyData2, getDailyData3, getDailyData4],
  (data0, data1, data2, data3, data4) => [data0, data1, data2, data3, data4],
)

const getPositionsMap = createSelector(
  [getSelectedGroup, getSelectedDate, getDetailsData],
  (selectedGroup, selectedDate, detailsData) => {
    const positions = {}

    detailsData.forEach(items => {
      items && forEach(items.positions, position => {
        positions[position.id] = {
          ...omit(position, ["economicExposure", "basePercentEquity", "positionPercentEquity"]),
          formatter: (value) => value ? numberFormatter(value) : "--",
          getEconomicExposures: (item, position) => {
            const data = find(item, { id: position })
            return pick(data, ["economicExposure", "basePercentEquity", "positionPercentEquity"])
          },
        }
      })
    })

    return positions
  },
)

export const getPositionsPagination = createSelector(
  [getPositionsPage, getPositionsMap],
  (positionsPage, positions) => {
    const total = keysIn(positions).length
    return {
      page: numberOfPositionsPerPage * positionsPage > total ? 0 : positionsPage,
      rowsPerPage: numberOfPositionsPerPage,
      total,
    }
  },
)

export const getPositionsSorting = createSelector(
  [getRawPositionsSorting, getDetailsData],
  (positionsSorting, detailsData) => {
    if (positionsSorting && (detailsData[positionsSorting.by] || positionsSorting.by === "id")) return positionsSorting

    return {
      by: "id",
      order: "asc",
    }
  },
)

const getPositionsDetails = createSelector(
  [getPositionsMap, getDetailsData, getPositionsSorting, getPositionsPagination, getPositionsDisplayType],
  (positions, detailsData, sorting, { page }, positionsDisplayType) => {
    if (positionsDisplayType.id === "all") {
      const [dir, absDir] = parseSortOrder(sorting.order)

      return chain(positions)
        .map(position => position)
        .orderBy([item => {
          if (sorting.by === "id") return item.id
          const data = get(detailsData[sorting.by], "positions", [])

          const economicExposure = get(find(data, { id: item.id }), "economicExposure", null)
          if (!economicExposure) {
            return dir === "asc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY
          }
          return absDir ? Math.abs(economicExposure) : economicExposure
        }], [dir])
        .slice(numberOfPositionsPerPage * page, (numberOfPositionsPerPage * page) + numberOfPositionsPerPage)
        .value()
    }

    return chain(detailsData)
      .map(item => {
        const detailsPositions = chain(item)
          .get("positions", [])
          .orderBy([item => Math.abs(item.economicExposure)], ["desc"])
          .take(10)
          .map(item => {
            const absValue = Math.abs(item.economicExposure)
            return {
              ...item,
              label: item.ticker,
              value: item.economicExposure,
              chartWidth: percentageFormatter(absValue < 100 ? absValue : 100, true),
            }
          })
          .value()

        if (item && item.positions.length > 10) {
          const top10Value = chain(detailsPositions).sumBy(item => Math.abs(item.value)).value()
          const absValue = Math.abs(top10Value)

          detailsPositions.unshift({
            label: "Top 10 positions",
            value: top10Value,
            chartWidth: percentageFormatter(absValue < 100 ? absValue : 100, true),
          })
        }

        return detailsPositions
      })
      .value()
  },
)

function getRefEquityLabel(context) {
  if (context && context.smartTrade && !context.equity) {
    return "Smart Trade Equity"
  }
  return "Reference Equity"
}

export const getDetailsItems = createSelector(
  [getDetailsData, getPositionsDetails, getMissingDataFiles, getModelId, getContext, getCurrency, getSelectedModelName],
  (detailsData, positions, missingDataFiles, modelId, context, currency, modelName) => {
    const dollarFormatter = makeCurrencyFormatter(currency)
    return {
      summary: [{
        label: "Gross Market Value",
        fn: (item, type) => !includes(["portfolio", "experiment", "inline"], type) ? "--" : dollarFormatter(get(item, "summary.gmv", 0)),
      }, {
        label: getRefEquityLabel(context),
        fn: item => {
          const value = get(item, "summary.equity")

          if (value) {
            return dollarFormatter(value)
          } else {
            return "--"
          }
        },
      }, {
        label: "Positions",
        fn: item => get(item, "summary.positionsCount", 0),
      }, {
        label: "Concentration",
        fn: item => `${numberFormatter(get(item, "summary.minConcentration", 0))} - ${percentageFormatter(get(item, "summary.maxConcentration", 0), true)}`,
      }, {
        label: "Model Coverage",
        fn: item => percentageFormatter(get(item, "summary.percentModeledGmv", 0)),
      }, {
        label: "Market Capitalization",
        fn: item => dollarFormatter(get(item, "summary.averageMarketCap", 0)),
      }],
      groups: chain(detailsData)
        .map(item => item ? item.groups : null)
        .value(),
      positions,
      ...getBetaDetails(missingDataFiles, modelId, modelName),
    }
  })

export function getBetaDetails(missingDataFiles, modelId, modelName) {
  const beta = {
    beta: [],
  }
  const { historical, predicted } = getBetaColumnsAndTooltips(modelName) || {}
  if (!betasAreMissing(missingDataFiles)) {
    if (!includes(missingDataFiles, "predictedBeta")) {
      beta.beta.push({
        label: `${get(predicted, "label")} Beta`,
        tooltipContents: get(predicted, "tooltip"),
        fn: item => get(item, "beta.predicted") ? numberFormatter(get(item, "beta.predicted")) : "--",
      })
    }
    if (!includes(missingDataFiles, "historicalBeta")) {
      beta.beta.push({
        label: `${get(historical, "label")} Beta`,
        tooltipContents: get(historical, "tooltip"),
        fn: item => get(item, "beta.historical") ? numberFormatter(get(item, "beta.historical")) : "--",
      })
    }
  }
  return beta
}

export const getTableDownloadData = createSelector(
  [getDetailsData, getDetailsItems, getSelectedGroup, getPositionsDisplayType, getItems],
  (detailsData, { summary, beta, positions }, selectedGroup, { label }, items) => ({
    sheets: [
      computeSummarySheetDownload(detailsData, items, [summary, beta]),
      computeGroupSheetDownload(detailsData, items, selectedGroup),
      computePositionsSheetDownload(detailsData, items, positions, label),
    ],
  }),
)
